import { useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Caption1, Field, Input, Text, makeStyles } from '@fluentui/react-components';
import type { Person } from '@vigeo/shared';
import { ShellIcons, StatusDot, complianceTokenFor, tokens } from '@vigeo/ui';
import { StoredFileLink } from '@/features/files';
import { fetchOnboardingPreview } from '@/lib/api';
import { ConfirmSendOnboarding } from './ConfirmSendOnboarding.js';
import { OfferTracker } from './OfferTracker.js';
import {
  moveCopy, moveLede, moveStepOne, offerTrackerSteps, stageSteps, type SignedOfferSummary,
} from './model.js';

const useStyles = makeStyles({
  card: {
    display: 'flex', flexDirection: 'column', minWidth: 0,
    backgroundColor: tokens.colorNeutralBackground1,
    border: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusLarge,
    overflow: 'hidden',
  },
  intro: {
    display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS,
    padding: `${tokens.spacingVerticalL} ${tokens.spacingHorizontalXL}`,
  },
  h: { margin: 0, fontSize: tokens.fontSizeBase600, lineHeight: tokens.lineHeightBase600, fontWeight: tokens.fontWeightSemibold },
  /**
   * The frame's label-column row: a 12.5rem description on the left, the fields on the right,
   * wrapping to one column when there is no room for two.
   */
  row: {
    display: 'flex', flexDirection: 'row', flexWrap: 'wrap', alignItems: 'flex-start',
    gap: tokens.spacingHorizontalXXL,
    padding: tokens.spacingHorizontalXL,
    borderTop: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
  },
  labelColumn: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS, flexBasis: '12.5rem', flexGrow: 0, minWidth: 0 },
  rowLabel: { fontWeight: tokens.fontWeightSemibold },
  muted: { color: tokens.colorNeutralForeground2 },
  fields: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM, flexBasis: '24rem', flexGrow: 1, minWidth: 0 },
  statusLine: { display: 'flex', flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: tokens.spacingHorizontalS },
  values: { display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: tokens.spacingHorizontalM },
  value: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS, flexBasis: '10rem', flexGrow: 1, minWidth: 0 },
  filedOffer: {
    display: 'flex', flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: tokens.spacingHorizontalS, minWidth: 0,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    border: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
  },
  filedIcon: {
    display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    backgroundColor: tokens.colorBrandBackground2, color: tokens.colorBrandForeground1,
    borderRadius: tokens.borderRadiusMedium, padding: tokens.spacingHorizontalXS,
  },
  filedText: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS, flexGrow: 1, minWidth: 0, overflowWrap: 'anywhere' },
  /** "Candidate → Onboarding", on the brand tint the frames use for a chosen state. */
  fromTo: {
    display: 'flex', flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: tokens.spacingHorizontalXS,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    backgroundColor: tokens.colorBrandBackground2,
    borderRadius: tokens.borderRadiusMedium,
  },
  fromToTarget: { color: tokens.colorBrandForeground1, fontWeight: tokens.fontWeightSemibold },
  strong: { fontWeight: tokens.fontWeightSemibold },
  icon: { display: 'flex', flexDirection: 'row', alignItems: 'center', color: tokens.colorNeutralForeground2 },
  step: { display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: tokens.spacingHorizontalS, minWidth: 0 },
  number: {
    display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0, width: '1.5rem', height: '1.5rem',
    border: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusCircular,
    color: tokens.colorNeutralForeground2,
  },
  stepBody: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS, flexGrow: 1, minWidth: 0 },
  footer: {
    display: 'flex', flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalL} ${tokens.spacingHorizontalXL}`,
    backgroundColor: tokens.colorNeutralBackground2,
    borderTop: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
  },
  footerNote: { flexGrow: 1, minWidth: 0, color: tokens.colorNeutralForeground2 },
});

export interface MoveToOnboardingProps {
  readonly person: Person;
  /** Built by `signedOfferSummary`, so this component never asks which kind of offer it has. */
  readonly offer: SignedOfferSummary;
  readonly firstName: string;
  readonly roleLabel: string | null;
  readonly canEdit: boolean;
  readonly busy: boolean;
  readonly onMove: (choice: { readonly reason: string; readonly email: boolean }) => void;
  readonly onCancel: () => void;
}

/**
 * Row 2.2 — moving a candidate with a filed offer to Onboarding (frame 06b `322:1186`, issue #264).
 *
 * The move itself is the existing `changeLifecycle` call and the existing ADR-011 gate; nothing
 * here relaxes either. What the frame adds is everything HR could not previously see before
 * clicking: which offer they are acting on, what the packet will contain, and that nothing is
 * emailed unless they ask for it.
 *
 * The preview comes from `planPacket` through `/onboarding-preview` — the same function
 * `issuePacket` creates from — so the chips cannot promise a packet the move will not produce.
 */
export function MoveToOnboarding({ person, offer, firstName, roleLabel, canEdit, busy, onMove, onCancel }: MoveToOnboardingProps) {
  const styles = useStyles();
  // Typed widely on purpose: `moveCopy` is `as const`, so inferring from the seed would pin the
  // state to that one literal and refuse anything HR actually types.
  const [reason, setReason] = useState<string>(moveCopy.defaultReason);
  const [email, setEmail] = useState(false);
  /**
   * 2.2 no longer sends; it ASKS (Paul, 2026-09-29 15:45). The button opens 2.3, and only
   * "Confirm and send" there calls `onMove`. The reason still belongs to this screen — it is
   * pre-filled, it goes to the audit trail, and `tests/offer-stage-rows.test.tsx` pins that it
   * never claims filing.
   */
  const [confirming, setConfirming] = useState(false);
  /** The button that opened the question, so the keyboard goes back there (Safari on a Mac does not focus a button on click). */
  const confirmOpener = useRef<HTMLElement | null>(null);
  const preview = useQuery({ queryKey: ['onboarding-preview', person.id], queryFn: () => fetchOnboardingPreview(person.id) });


  return (
    <section className={styles.card} aria-label={moveCopy.title}>
      <div className={styles.intro}>
        <h3 className={styles.h}>{moveCopy.title}</h3>
        <Text>{moveLede(firstName)}</Text>
      </div>

      <div className={styles.row}>
        <div className={styles.labelColumn}>
          <Text className={styles.rowLabel}>{moveCopy.offerLabel}</Text>
          <Caption1 className={styles.muted}>{moveCopy.offerCaption}</Caption1>
        </div>
        <div className={styles.fields}>
          <div className={styles.statusLine}>
            {offer.badgeLabel === null ? null : (
              <StatusDot color={offer.badgeTone === 'good' ? complianceTokenFor.good : complianceTokenFor.warning}>{offer.badgeLabel}</StatusDot>
            )}
            {offer.approvedLine === null ? null : <Caption1 className={styles.muted}>{offer.approvedLine}</Caption1>}
          </div>
          <OfferTracker steps={offerTrackerSteps} reached={offerTrackerSteps.length - 1} label="Offer progress" />
          {/* A paper-recorded offer has no typed terms, so the whole cell row is omitted rather
              than printed as three dashes. */}
          {offer.position === null ? null : (
            <div className={styles.values}>
              <div className={styles.value}>
                <Caption1 className={styles.muted}>{moveCopy.positionLabel}</Caption1>
                <Text>{offer.position}</Text>
              </div>
              <div className={styles.value}>
                <Caption1 className={styles.muted}>{moveCopy.proposedStartLabel}</Caption1>
                <Text>{offer.proposedStart}</Text>
              </div>
              <div className={styles.value}>
                <Caption1 className={styles.muted}>{moveCopy.ratesLabel}</Caption1>
                {/* One line per compensation row, as the frame stacks "$25.00 per visit" over
                    "$0.70 per mile" — an offer may carry up to 25. */}
                {offer.rates.map((line) => <Text key={line}>{line}</Text>)}
              </div>
            </div>
          )}
          <div className={styles.filedOffer}>
            <span className={styles.filedIcon}><ShellIcons.document /></span>
            <div className={styles.filedText}>
              <Text className={styles.strong}>{offer.fileName}</Text>
              {offer.signedLine === null ? null : <Caption1 className={styles.muted}>{offer.signedLine}</Caption1>}
            </div>
            <StoredFileLink personId={person.id} storedFileId={offer.storedFileId} legacyUrl={offer.legacyUrl} label="View PDF" />
          </div>
        </div>
      </div>

      <div className={styles.row}>
        <div className={styles.labelColumn}>
          <Text className={styles.rowLabel}>{moveCopy.stageLabel}</Text>
          <Caption1 className={styles.muted}>{moveCopy.stageCaption}</Caption1>
        </div>
        <div className={styles.fields}>
          <OfferTracker
            steps={stageSteps.map((s) => s.label)}
            captions={stageSteps.map((s) => s.caption)}
            reached={0}
            label="Stage"
          />
          <div className={styles.fromTo}>
            <Text className={styles.strong}>{stageSteps[0]!.label}</Text>
            <span className={styles.icon}><ShellIcons.arrowRight /></span>
            <Text className={styles.fromToTarget}>{stageSteps[1]!.label}</Text>
          </div>
          <Field label={moveCopy.reasonLabel} required hint={moveCopy.reasonHint}>
            <Input value={reason} disabled={!canEdit || busy} onChange={(_, d) => setReason(d.value)} />
          </Field>
        </div>
      </div>

      <div className={styles.row}>
        <div className={styles.labelColumn}>
          <Text className={styles.rowLabel}>{moveCopy.whatHappensLabel}</Text>
        </div>
        <div className={styles.fields}>
          <div className={styles.step}>
            <Caption1 className={styles.number}>1</Caption1>
            <div className={styles.stepBody}><Text>{moveStepOne(firstName)}</Text></div>
          </div>

          {/*
            STEPS 2 AND 3 MOVED TO THE CONFIRM WINDOW (2.3, Paul 2026-09-29 15:45; #373).

            What is sent — the packet, that form 3.6 comes later at orientation, that the portal
            link is unchanged, and the Welcome email choice — now appears behind Send Onboarding
            Items rather than inline here. `ConfirmSendOnboarding` renders the SAME copy helpers,
            so the two cannot disagree about how many forms a candidate is about to receive.
          */}
        </div>
      </div>

      <div className={styles.footer}>
        <Caption1 className={styles.footerNote}>{moveCopy.footerNote}</Caption1>
        {canEdit ? (
          <>
            <Button disabled={busy} onClick={onCancel}>{moveCopy.cancel}</Button>
            {/*
              Opens 2.3 rather than sending. The blank-reason gate is duplicated HERE and on 2.3's
              confirm: this one stops a pointless trip into a window that cannot send, and that one
              guards the action itself. `changeLifecycleSchema` now refuses a whitespace reason too —
              it did not until 2026-09-30, and a comment here asserted that it did.
            */}
            <Button appearance="primary" disabled={busy || reason.trim() === ''} onClick={(event) => { confirmOpener.current = event.currentTarget; setConfirming(true); }}>
              {moveCopy.move}
            </Button>
          </>
        ) : null}
      </div>

      <ConfirmSendOnboarding
        open={confirming}
        opener={confirmOpener}
        firstName={firstName}
        roleLabel={roleLabel}
        candidateEmail={person.contact.email}
        /* undefined while loading, null when it could not be worked out — the dialog says which. */
        preview={preview.isPending ? undefined : preview.isError ? null : preview.data}
        email={email}
        onEmailChange={setEmail}
        busy={busy}
        canSend={reason.trim() !== ''}
        onCancel={() => setConfirming(false)}
        /**
         * CLOSES BEFORE IT SENDS, and both halves of that matter.
         *
         * It used to only call `onMove`. Nothing closed 2.3, so a successful send left 2.4 stacked on
         * top of a still-open 2.3 — two `aria-modal` dialogs — and once the mutation settled `busy`
         * went false while 2.2 was still mounted, re-enabling this dialog's own "Confirm and send". An
         * adversarial review demonstrated a second click issuing a SECOND `changeLifecycle` and a
         * second welcome email. `busy` guards in-flight, not settled-but-not-yet-unmounted, and the
         * window stayed open for the whole round trip — indefinitely if the refetch failed.
         *
         * It also hid refusals: `notice` renders outside the dialog, so a refused send put the reason
         * under a backdrop while the modal still asked HR to confirm.
         *
         * A BLANK REASON IS REFUSED HERE TOO. The gate used to sit only on the button that OPENS this
         * window — one screen away from the action it guards — and the comment beside it claimed the
         * API refused one as well. It does not: `changeLifecycleSchema` is `z.string().min(1)`, which
         * accepts "   ". The gate now sits on the sending action, where the invariant is.
         */
        onConfirm={() => {
          if (reason.trim() === '') return;
          setConfirming(false);
          onMove({ reason, email });
        }}
      />
    </section>
  );
}
